import api from './api';
import { ChatRequest, ChatResponse, SnippetRequest, SnippetResponse } from '../types';

// Helper function to check if it's a network error
const isNetworkError = (error: any): boolean => {
  return !error.response && (error.code === 'NETWORK_ERROR' || error.message?.includes('Network Error') || error.message?.includes('ERR_NETWORK'));
};

export const chatApi = {
  // Send chat message to entity (regular chat)
  sendMessage: async (chatData: ChatRequest): Promise<ChatResponse> => {
    try {
      
      const response = await api.post('/chat', chatData);
      
      return response.data;
    } catch (error: any) {
      
      if (error.response?.status === 422) {
        const detail = error.response.data?.detail;
        if (Array.isArray(detail)) {
          throw new Error(detail.map((err: any) => err.msg).join(', '));
        }
        throw new Error('Invalid chat message');
      }
      if (error.response?.status === 404) {
        throw new Error('Entity not found');
      }
      if (isNetworkError(error)) {
        throw new Error('Unable to connect to server. Please check your internet connection or try again later.');
      }
      throw new Error(error.response?.data?.detail || 'Failed to send message');
    }
  },

  // Streaming chat with Server-Sent Events
  streamChat: async (chatData: ChatRequest, onMessage: (data: any) => void, onError: (error: string) => void, onComplete: () => void): Promise<void> => {
    try {
      
      
      // Create EventSource for streaming
      const token = localStorage.getItem('token');
      const url = `${api.defaults.baseURL}/chat/stream`;
      
      // Add timeout protection
      const timeoutId = setTimeout(() => {
        
        onError('Response took too long. Please try again.');
        onComplete();
      }, 30000); // 30 second timeout
      
      // Use fetch with streaming for better control
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(chatData),
      });

      if (!response.ok) {
        clearTimeout(timeoutId);
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      if (!response.body) {
        clearTimeout(timeoutId);
        throw new Error('No response body for streaming');
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let accumulatedContent = '';
      let lastActivityTime = Date.now();

      try {
        while (true) {
          const { done, value } = await reader.read();
          
          if (done) {
            
            clearTimeout(timeoutId);
            onComplete();
            break;
          }

          const chunk = decoder.decode(value, { stream: true });
          const lines = chunk.split('\n');

          for (const line of lines) {
            if (line.startsWith('data: ')) {
              try {
                const data = JSON.parse(line.slice(6));
                
                
                // Update activity time
                lastActivityTime = Date.now();
                
                // Handle different streaming data formats
                if (data.status === 'processing') {
                  // Show processing message
                  onMessage({
                    response: data.message || 'Processing your question...',
                    confidence_score: 0,
                    entity_name: '',
                    isProcessing: true
                  });
                } else if (data.status === 'complete') {
                  // Final response
                  clearTimeout(timeoutId);
                  onMessage({
                    response: data.response || accumulatedContent,
                    confidence_score: data.confidence_score || 0,
                    entity_name: data.entity_name || '',
                    isComplete: true
                  });
                  onComplete();
                  return; // Exit early on completion
                } else if (data.status === 'error' || data.status === 'failed') {
                  // Handle error status
                  clearTimeout(timeoutId);
                  onError(data.message || 'Failed to process your question');
                  onComplete();
                  return;
                } else if (data.response) {
                  // Accumulate streaming text - this is the actual AI response content
                  accumulatedContent += data.response;
                  onMessage({
                    response: accumulatedContent,
                    confidence_score: data.confidence_score || 0,
                    entity_name: data.entity_name || '',
                    isStreaming: true
                  });
                } else if (data.message && !data.status) {
                  // Handle direct message content
                  accumulatedContent += data.message;
                  onMessage({
                    response: accumulatedContent,
                    confidence_score: data.confidence_score || 0,
                    entity_name: data.entity_name || '',
                    isStreaming: true
                  });
                }
              } catch (parseError) {
                
              }
            }
          }
          
          // Check for inactivity timeout
          if (Date.now() - lastActivityTime > 10000) { // 10 seconds of inactivity
            
            clearTimeout(timeoutId);
            onError('Response stopped. Please try again.');
            onComplete();
            return;
          }
        }
      } finally {
        reader.releaseLock();
        clearTimeout(timeoutId);
      }
    } catch (error: any) {
      
      if (error.message?.includes('Failed to fetch')) {
        onError('Unable to connect to server. Please check your internet connection.');
      } else {
        onError(error.message || 'Failed to start streaming chat');
      }
      onComplete();
    }
  },

  // Get chat status for an entity
  getChatStatus: async (entityId: string): Promise<any> => {
    try {
      
      const response = await api.get(`/chat/status/${entityId}`);
      
      return response.data;
    } catch (error: any) {
      
      if (error.response?.status === 404) {
        throw new Error('Entity not found');
      }
      if (isNetworkError(error)) {
        throw new Error('Unable to connect to server. Please check your internet connection.');
      }
      throw new Error(error.response?.data?.detail || 'Failed to get chat status');
    }
  },

  // Generate JavaScript snippet for entity
  generateSnippet: async (request: SnippetRequest): Promise<SnippetResponse> => {
    try {
      
      const response = await api.post(`/snippets/generate?entity_id=${request.entity_id}`);
      
      return response.data;
    } catch (error: any) {
      
      if (error.response?.status === 404) {
        throw new Error('Entity not found');
      }
      if (isNetworkError(error)) {
        throw new Error('Unable to connect to server. Please check your internet connection.');
      }
      throw new Error(error.response?.data?.detail || 'Failed to generate snippet');
    }
  },
};
